import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Coordinates, PrayerTimes as AdhanTimes, Qibla } from "adhan";
import tzLookup from "@photostructure/tz-lookup";
import { useLang } from "@/context/LangContext";
import { useProgress } from "@/context/ProgressContext";
import { useAlerts } from "@/context/AlertsContext";
import { KAABA, haversineKm } from "@/lib/geo";
import { reversePlace } from "@/lib/geocode";
import { PRAYERS, IMSAK_LABEL, DEFAULT_METHOD, resolveMethod, getParams, imsakFrom, nextPrayer, fmtCountdown, zoneDate, zoneOffsetLabel } from "@/lib/prayerTimes";
import { readFasting, writeFasting, isFastingDay } from "@/lib/fasting";

const PrayerScheduleContext = createContext(null);
const NOTIFY_KEYS = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
const DEVICE_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

// Single source of truth for the prayer schedule: one shared device position (AlertsContext, persisted),
// one method, one clock, one time zone (the LOCATION's zone — a pilgrim in Makkah whose phone is still on
// WIB sees Makkah clock times). The dashboard card and the full /times page read the very same values.
export function PrayerScheduleProvider({ children }) {
  const { lang } = useLang();
  const { progress } = useProgress();
  const { pos, updatePos } = useAlerts();
  const [located, setLocated] = useState(null); // null = locating, true = GPS fix, false = denied/unavailable
  const [place, setPlace] = useState(null);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [fasting, setFastingState] = useState(readFasting); // { on, schedule, lead } — Imsak & sahur reminders
  const setFasting = useCallback((patch) => setFastingState((f) => writeFasting({ ...f, ...patch })), []);

  const locate = useCallback((fresh = false) => {
    setLocated(null);
    if (!navigator.geolocation) { setLocated(false); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { updatePos([p.coords.latitude, p.coords.longitude]); setLocated(true); },
      () => setLocated(false),
      { enableHighAccuracy: false, maximumAge: fresh ? 0 : 300000, timeout: 8000 }
    );
  }, [updatePos]);
  useEffect(() => { locate(); }, [locate]);
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => {
    if (!pos) return;
    let cancelled = false;
    setPlaceLoading(true);
    reversePlace(pos, lang).then((p) => { if (!cancelled) { setPlace(p); setPlaceLoading(false); } });
    return () => { cancelled = true; };
  }, [pos, lang]);

  const methodId = progress?.calc_method || DEFAULT_METHOD;
  const here = pos || KAABA; // last known device position, else the Ka'bah
  const resolvedMethod = resolveMethod(methodId, here[0], here[1]);
  const tz = useMemo(() => { try { return tzLookup(here[0], here[1]); } catch { return DEVICE_TZ; } }, [here[0], here[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  const tzOffset = zoneOffsetLabel(now, tz);
  const deviceOffset = zoneOffsetLabel(now, DEVICE_TZ);
  const tzDiffers = tzOffset !== deviceOffset;
  const today = zoneDate(now, tz); // calendar date at the location
  const dayKey = today.toDateString();
  const fastingToday = fasting.on && isFastingDay(today, fasting.schedule);
  const showImsak = resolvedMethod === "Kemenag" || fasting.on; // fasting mode: Imsak (Fajr − 10 min) for every method

  const { times, list, qiblaBearing } = useMemo(() => {
    const c = new Coordinates(here[0], here[1]);
    const pt = new AdhanTimes(c, new Date(dayKey), getParams(resolvedMethod));
    const t = { imsak: showImsak ? imsakFrom(pt.fajr) : null, fajr: pt.fajr, sunrise: pt.sunrise, dhuhr: pt.dhuhr, asr: pt.asr, maghrib: pt.maghrib, isha: pt.isha };
    const list = PRAYERS.map((p) => ({ ...p, at: t[p.key] }));
    if (fasting.on) list.unshift({ key: "imsak", label: IMSAK_LABEL, at: t.imsak });
    return { times: t, list, qiblaBearing: Qibla(c) };
  }, [here[0], here[1], dayKey, resolvedMethod, showImsak, fasting.on]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = useMemo(() => nextPrayer(here, methodId, now), [here[0], here[1], methodId, now]); // eslint-disable-line react-hooks/exhaustive-deps
  const remaining = next ? next.at - now : null;
  const countdown = next ? fmtCountdown(remaining) : "";
  const km = pos ? haversineKm(here, KAABA) : null;

  // Push payload: "soon" (lead minutes before) + exact-time entries for today and tomorrow.
  const buildReminders = useCallback(({ lead, t, tlabel }) => {
    const c = new Coordinates(here[0], here[1]);
    const items = [];
    const nowMs = Date.now();
    for (let dayOffset = 0; dayOffset <= 1; dayOffset++) {
      const d = new Date(dayKey); d.setDate(d.getDate() + dayOffset);
      const pt = new AdhanTimes(c, d, getParams(resolvedMethod));
      NOTIFY_KEYS.forEach((k) => {
        const prayer = tlabel(PRAYERS.find((p) => p.key === k)?.label);
        const at = pt[k];
        [{ ms: at.getTime() - lead * 60000, soon: true }, { ms: at.getTime(), soon: false }].forEach(({ ms, soon }) => {
          if (ms <= nowMs) return;
          items.push({
            send_at: new Date(ms).toISOString(),
            title: soon ? t("notifSoonTitle", { prayer }) : t("notifNowTitle", { prayer }),
            body: soon ? t("notifSoonBody", { min: lead, prayer }) : t("notifNowBody", { prayer }),
            url: "/times",
          });
        });
      });
    }
    return items;
  }, [here[0], here[1], dayKey, resolvedMethod]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({
    pos, here, located, place, placeLoading, locate, now, today, methodId, resolvedMethod,
    tz, tzOffset, deviceTz: DEVICE_TZ, deviceOffset, tzDiffers,
    times, list, next, remaining, countdown, qiblaBearing, km, buildReminders, NOTIFY_KEYS,
    fasting, setFasting, fastingToday, showImsak,
  }), [pos, here, located, place, placeLoading, locate, now, today, methodId, resolvedMethod, tz, tzOffset, deviceOffset, tzDiffers, times, list, next, remaining, countdown, qiblaBearing, km, buildReminders, fasting, setFasting, fastingToday, showImsak]);

  return <PrayerScheduleContext.Provider value={value}>{children}</PrayerScheduleContext.Provider>;
}

export const usePrayerSchedule = () => useContext(PrayerScheduleContext);
